import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import { CheckoutProvider } from "@stripe/react-stripe-js/checkout";
import { loadStripe } from "@stripe/stripe-js";
import { Modal } from "../../components/ui";
import BillingAction from "../../actions/BillingAction";
import { billingCheckoutSession, billingSetFreePlan } from "../../api/BillingApi";
import LayoutAction from "../../actions/LayoutAction";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import { accessToken, getBaseUrl, getStripePublicKey } from "../../api/api";
import CheckoutForm from "./CheckoutForm";
import { CircleCheckBig, CircleX } from "lucide-react";
import RuleAction from "../../actions/RuleAction";
import { AppContext } from "../../Contexts";

const SelectPlanModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId = "" }: any = useParams();
  const appContext = useContext<any>(AppContext);

  const isChrome = appContext.appName == "chrome";

  const subscriptionStatus =
    useSelector((state: any) => state.billingReducer.get("subscriptionStatus")) || fromJS({});
  const plans = useSelector((state: any) => state.billingReducer.get("plans")) || fromJS([]);
  const checkoutSession =
    useSelector((state: any) => state.billingReducer.get("checkoutSession")) || fromJS({});
  const showSelectPlan = useSelector(
    (state: any) => state.billingReducer.get("showSelectPlan") || false
  );

  const appLinks = useSelector((state: any) => state.ruleReducer.get("appLinks"));

  const currentPlanKey = subscriptionStatus.get("planKey");
  const currentPlan = plans.find((item) => item.get("key") === currentPlanKey) || fromJS({});

  const publishableKey = getStripePublicKey();
  const clientSecret = checkoutSession.get("clientSecret");

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  useEffect(() => {
    dispatch(RuleAction.getAppLinks());
  }, [dispatch]);

  const handleSetFreePlan = (plan) => {
    setShowConfirm({
      show: true,
      title: t("Set Free Plan"),
      description: (
        <div>
          <div>{t("Are you sure you would like to set free plan?")}</div>
          <br />
          <div className="font-bold">
            {t(
              "Your current plan will be used until the end of the month and automatically switched to the free plan."
            )}
          </div>
        </div>
      ),
      type: "SET_FREE_PLAN",
      data: plan.get("key"),
    });
  };

  const handleCheckoutSession = async (plan) => {
    if (isChrome) {
      const token = await accessToken();
      window.open(
        `${getBaseUrl()}/workspaces/${projectId}/settings/team?item=billing&key=Team%20Settings&showChangePlan=true&jwt=${encodeURIComponent(token)}`,
        "_blank"
      );
    } else {
      billingCheckoutSession({
        planKey: plan.get("key"),
        trialPeriodDays: 30,
      })
        .then((data) => {
          if (data && data.clientSecret) {
            dispatch(
              BillingAction.setCheckoutSession({
                ...data,
                selectedPlan: plan.toJS(),
              })
            );
          }
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Checkout Session"),
              type: "error",
              message: t("Failed to checkout session"),
            })
          );
        });
    }
  };

  const handleSubmit = () => {
    if (showConfirm.type === "SET_FREE_PLAN") {
      billingSetFreePlan(showConfirm.data)
        .then(() => {
          dispatch(BillingAction.getBillingUserLimits());
          dispatch(BillingAction.getBillingSubscriptionStatus());
          setShowConfirm({ show: false, title: "", description: "" });
          dispatch(BillingAction.hideSelectPlanDialog());
          dispatch(
            LayoutAction.setNotification({
              title: t("Set Free Plan"),
              type: "success",
              message: t("Updated free plan"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Set Free Plan"),
              type: "error",
              message: t("Failed to set free plan"),
            })
          );
        });
    }
  };

  const handleClose = () => {
    dispatch(BillingAction.hideSelectPlanDialog());
  };

  const formatNumberValue = (value?: number | string) => {
    if (value === null || value === undefined || value === "") {
      return t("None");
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return String(value);
    }
    if (numeric === 0) {
      return t("None");
    }
    return numeric === 1 ? `${t("Only")} 1` : `${t("Up to")} ${numeric.toLocaleString()}`;
  };

  const formatAutomationLimit = (value?: number | string | null) => {
    if (value === null || value === undefined || value === "") {
      return t("Unlimited");
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return t("Unlimited");
    }
    if (numeric >= 2147483647) {
      return t("Unlimited");
    }
    if (numeric <= 0) {
      return t("None");
    }
    return `${numeric.toLocaleString()} ${t("runs/month")}`;
  };

  const Check = () => <CircleCheckBig className="w-5 h-5 text-blue-600" />;
  const Cross = () => <CircleX className="w-5 h-5 text-gray-400" />;

  const renderImageIcon = (appId) => {
    const app = appLinks.toJS().find((item) => item.app == appId);
    return (
      <img className="w-4 h-4 object-contain mr-2" src={`${getBaseUrl()}${app?.imageUrl}`} alt="" />
    );
  };

  const renderSelectPlan = () => {
    return (
      <div className="max-w-6xl">
        <div className="grid grid-cols-5 gap-4">
          <div className="p-4 bg-gray-100">
            <div className="h-50">
              <h2 className="text-2xl font-semibold mb-6">{t("Compare plans")}</h2>
              <p className="text-gray-500 mb-12">{t("Find the right plan for you")}</p>
            </div>
            <div className="space-y-3 text-sm text-gray-700">
              <p className="text-xl font-semibold">{t("Teams")}</p>
              <p>{t("Member limit")}</p>
              <p>{t("Pipeline limit")}</p>
              <p>{t("Social posts/day")}</p>
              <p>{t("AI tokens/month")}</p>
              <p>{t("Automation runs/month")}</p>
              <h4 className="mt-6 text-xl font-semibold">{t("Integrations")}</h4>
              <div className="space-y-2 pl-2">
                <p className="flex items-center">{renderImageIcon("jsm")}Jira</p>
                <p className="flex items-center">{renderImageIcon("google")}Google Workspace</p>
                <p className="flex items-center">{renderImageIcon("azure")}Microsoft 365</p>
                <p className="flex items-center">{renderImageIcon("facebook")}Facebook</p>
                <p className="flex items-center">{renderImageIcon("twitter")}Twitter</p>
                <p className="flex items-center">{renderImageIcon("instagram")}Instagram</p>
                <p className="flex items-center">{renderImageIcon("linkedin")}LinkedIn</p>
                <p className="flex items-center">{renderImageIcon("threads")}Threads</p>
                <p className="flex items-center">{renderImageIcon("slack")}Slack</p>
                <p className="flex items-center">{renderImageIcon("canva")}Canva</p>
              </div>
              <h4 className="mt-6 text-xl font-semibold">{t("Features")}</h4>
              <div className="space-y-2 pl-2">
                <p>CRM</p>
                <p>Goals</p>
                <p>Reports</p>
                <p>Developer API</p>
                <p>Automation</p>
                <p>{t("Agent loop (multi-turn AI)")}</p>
              </div>
            </div>
          </div>
          {plans.map((item, index) => {
            let title = t("Set Plan");
            let className = "";
            const currentPlanIndex = plans.findIndex((item) => item.get("key") === currentPlanKey);
            if (currentPlanIndex === index) {
              title = t("Current Plan");
              className = "bg-blue-600 text-white border-blue-600";
            } else if (currentPlanIndex < index) {
              title = t("Upgrade");
              className = "cursor-pointer bg-blue-600 text-white border-blue-600";
            } else if (currentPlanIndex > index) {
              title = t("Downgrade");
              className = "cursor-pointer text-gray-700 border-gray-300";
            }
            const isEnterprise = item.get("key") === "ENTERPRISE_UNLIMITED";
            let price: number | null = 0;
            switch (item.get("key")) {
              case "FREE_1_USER":
                price = 0;
                break;
              case "PLUS_5_USERS":
                price = 50;
                break;
              case "PLUS_25_USERS":
                price = 100;
                break;
              case "ENTERPRISE_UNLIMITED":
                price = null;
                break;
              default:
                price = 0;
                break;
            }
            return (
              <div key={item.get("key")} className="p-4">
                <div className="h-50">
                  <h3 className="text-xl font-semibold">{item.get("name")}</h3>
                  <p className="mt-4">
                    {price !== null ? (
                      <>
                        <span className="text-4xl font-bold">{`$${price}`}</span>
                        <span className="text-sm text-gray-500">/{t("month")}</span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold">{t("Contact us")}</span>
                    )}
                  </p>
                  {price !== null && (
                    <>
                      <p className="text-sm text-gray-500">{t("per team")},</p>
                      <p className="text-sm text-gray-500">{t("billed monthly")}</p>
                    </>
                  )}
                  <button
                    onClick={() => {
                      if (item.get("key") === "FREE_1_USER") {
                        handleSetFreePlan(item);
                      } else if (item.get("key") === "ENTERPRISE_UNLIMITED") {
                        window.open(
                          "https://jsoftconnector.atlassian.net/servicedesk/customer/portal/1",
                          "_blank"
                        );
                      } else {
                        handleCheckoutSession(item);
                      }
                    }}
                    className={`mt-4 px-2 py-1 rounded-xl text-sm font-medium disabled:opacity-50 border ${className}`}
                    disabled={item.get("key") === currentPlanKey}
                  >
                    {isEnterprise && item.get("key") !== currentPlanKey ? t("Contact us") : title}
                  </button>
                </div>
                <div className="space-y-3 text-sm text-gray-700">
                  <p className="text-xl font-semibold">&nbsp;</p>
                  <p>{formatNumberValue(item.getIn(["entitlements", "maxUsersPerTenant"]))}</p>
                  <p>{formatNumberValue(item.getIn(["entitlements", "maxPipelines"]))}</p>
                  <p>
                    {formatNumberValue(item.getIn(["entitlements", "maxScheduledPostsPerDay"]))}
                  </p>
                  <p>
                    {item.getIn(["entitlements", "aiContentTokensPerMonth"])
                      ? `${formatNumberValue(
                          item.getIn(["entitlements", "aiContentTokensPerMonth"])
                        )} tokens`
                      : t("None")}
                  </p>
                  <p>
                    {formatAutomationLimit(
                      item.getIn(["entitlements", "maxAutomationExecutionsPerMonth"])
                    )}
                  </p>
                  <h4 className="mt-6 text-xl font-semibold">&nbsp;</h4>
                  <div className="space-y-2 pl-2">
                    <p>
                      {item.getIn(["entitlements", "featuresEnabled", "jiraIntegration"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "googleWorkspace"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "microsoft365"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "facebook"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "twitter"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "instagram"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "linkedIn"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "threads"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "canva"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "integrationsEnabled", "slack"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                  </div>
                  <h4 className="mt-6 text-xl font-semibold">&nbsp;</h4>
                  <div className="space-y-2 pl-2">
                    <p>
                      <Check />
                    </p>
                    <p>
                      <Check />
                    </p>
                    <p>
                      <Check />
                    </p>
                    <p>
                      {item.getIn(["entitlements", "featuresEnabled", "apiAccess"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "featuresEnabled", "automation"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                    <p>
                      {item.getIn(["entitlements", "featuresEnabled", "chatAgentLoop"]) ? (
                        <Check />
                      ) : (
                        <Cross />
                      )}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (publishableKey && clientSecret) {
      return (
        <CheckoutProvider
          stripe={loadStripe(publishableKey)}
          options={{ clientSecret: clientSecret }}
        >
          <CheckoutForm />
        </CheckoutProvider>
      );
    }
    return renderSelectPlan();
  };

  return (
    <>
      <Modal
        isOpen={showSelectPlan}
        onClose={handleClose}
        title={clientSecret ? t("Payment Details") : t("Change Plan")}
        subtitle={
          clientSecret
            ? t("Please fill in the form to make payment")
            : `${t("You are currently on the")} ${currentPlan.get("name")}`
        }
        position="center"
        className="w-auto max-w-[1200px] max-h-[98vh]"
      >
        <div className="p-6">{renderContent()}</div>
      </Modal>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={<OctagonAlertIcon />}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </>
  );
};

export default SelectPlanModal;
